// [PURPOSE]: IndexedDB-backed blob store for character images (portraits, item
//   icons, and gallery entries). Pulls dataUrl payloads OUT of localStorage so
//   character saves don't blow the ~5MB localStorage quota or the ~1MB
//   Firestore document limit when sync lands.
//
// [DESIGN]: Synchronous reads after one-time hydration. App.jsx calls
//   hydrateAll() on startup; after that, getCachedImage(id) returns the
//   dataUrl synchronously so React components don't need an async resolver.
//
// [INTEGRATION]: Save-side helpers (stripImagesFromState / restoreImagesIntoState
//   in src/utils/imageStateSerializer.js) lift dataUrls into IDB at autosave
//   time and inflate IDs back to dataUrls at load time. Existing components
//   continue to render data URLs from React state — only the persisted payload
//   stores compact IDs.
//
// [FALLBACK]: Per user spec — if IDB write fails (private browsing, quota,
//   API missing), the same data is mirrored into localStorage under the
//   `img-fallback-<id>` namespace so images aren't lost.

const DB_NAME = 'tti-images';
const STORE_NAME = 'blobs';
const DB_VERSION = 1;
const FALLBACK_PREFIX = 'img-fallback-';

let dbPromise = null;
const memCache = new Map(); // id -> dataUrl
const metaCache = new Map(); // id -> { type, prompt, createdAt, ... }
let hydrated = false;

function hasIDB() {
  return typeof indexedDB !== 'undefined';
}

function openDB() {
  if (dbPromise) return dbPromise;
  if (!hasIDB()) {
    dbPromise = Promise.reject(new Error('IndexedDB not available'));
    return dbPromise;
  }
  dbPromise = new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(err);
      return;
    }
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IDB open failed'));
    req.onblocked = () => reject(new Error('IDB open blocked'));
  });
  // Reset on failure so next call can retry.
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

function tx(mode, fn) {
  return openDB().then((db) => new Promise((resolve, reject) => {
    let result;
    const t = db.transaction(STORE_NAME, mode);
    const store = t.objectStore(STORE_NAME);
    try {
      const req = fn(store);
      if (req && typeof req.onsuccess !== 'undefined') {
        req.onsuccess = () => { result = req.result; };
        req.onerror = () => reject(req.error);
      } else {
        result = req;
      }
    } catch (e) {
      reject(e);
      return;
    }
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('Transaction aborted'));
  }));
}

function fallbackKey(id) {
  return `${FALLBACK_PREFIX}${id}`;
}

function readFallback(id) {
  try {
    return localStorage.getItem(fallbackKey(id));
  } catch (_) {
    return null;
  }
}

function writeFallback(id, dataUrl) {
  try {
    localStorage.setItem(fallbackKey(id), dataUrl);
    return true;
  } catch (_) {
    return false;
  }
}

function deleteFallback(id) {
  try {
    localStorage.removeItem(fallbackKey(id));
  } catch (_) { /* ignore */ }
}

/**
 * [PURPOSE]: Generate a unique ID for a new image. Used by producer points
 *   when minting an ID for newly-created (uploaded or generated) images.
 */
export function generateImageId() {
  const rand = Math.random().toString(36).slice(2, 9);
  return `img_${Date.now()}_${rand}`;
}

/**
 * [PURPOSE]: Derive a STABLE id from a dataUrl using a length+head+tail
 *   fingerprint hashed via djb2. Two calls with the same dataUrl produce the
 *   same id, so save-time stripping is deterministic — critical for the
 *   autosave dedup check (`serialized === lastSavedSerializedRef.current`).
 *
 * [DESIGN]: Sample-based fingerprint avoids hashing megabytes of base64 on
 *   every save tick. Length + first 128 + last 64 chars are stable per
 *   content; collisions across different real images are extremely unlikely.
 */
function djb2(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

export function deriveStableId(dataUrl) {
  if (typeof dataUrl !== 'string' || dataUrl.length === 0) return null;
  const len = dataUrl.length;
  const head = dataUrl.slice(0, 128);
  const tail = dataUrl.slice(Math.max(128, len - 64));
  const fp = `${len}:${head}:${tail}`;
  return `img_h${djb2(fp)}`;
}

/**
 * [PURPOSE]: Persist an image dataUrl to IDB (with localStorage fallback).
 *   Returns the id under which it was stored — pass through to React state.
 */
export async function putImage(id, dataUrl, metadata = {}) {
  if (!id || !dataUrl || typeof dataUrl !== 'string') {
    throw new Error('putImage: id and dataUrl required');
  }
  const record = {
    id,
    dataUrl,
    type: metadata.type || 'unknown',
    prompt: metadata.prompt || '',
    createdAt: metadata.createdAt || new Date().toISOString(),
    characterName: metadata.characterName,
  };
  memCache.set(id, dataUrl);
  metaCache.set(id, { ...record });
  if (!hasIDB()) {
    writeFallback(id, dataUrl);
    return id;
  }
  try {
    await tx('readwrite', (s) => s.put(record));
    deleteFallback(id);
    return id;
  } catch (err) {
    console.warn('[imageBlobStore] IDB write failed, using localStorage fallback', err);
    writeFallback(id, dataUrl);
    return id;
  }
}

/**
 * [PURPOSE]: Return the dataUrl for a given image id from the in-memory cache.
 *   Returns null if not hydrated or not present. Designed to be called
 *   synchronously by React render paths after App startup hydration.
 */
export function getCachedImage(id) {
  if (!id) return null;
  return memCache.get(id) || null;
}

/**
 * [PURPOSE]: Async fetch for an image by id. Falls back to localStorage on
 *   IDB miss. Used by the gallery export flow and any consumer that runs
 *   before hydration completes.
 */
export async function getImageAsync(id) {
  if (!id) return null;
  if (memCache.has(id)) return memCache.get(id);
  if (hasIDB()) {
    try {
      const rec = await tx('readonly', (s) => s.get(id));
      if (rec && rec.dataUrl) {
        memCache.set(id, rec.dataUrl);
        metaCache.set(id, rec);
        return rec.dataUrl;
      }
    } catch (_) { /* fall through to fallback */ }
  }
  const fb = readFallback(id);
  if (fb) {
    memCache.set(id, fb);
    return fb;
  }
  return null;
}

/**
 * [PURPOSE]: Remove an image from IDB + fallback + cache.
 */
export async function deleteImage(id) {
  if (!id) return;
  memCache.delete(id);
  metaCache.delete(id);
  deleteFallback(id);
  if (!hasIDB()) return;
  try {
    await tx('readwrite', (s) => s.delete(id));
  } catch (err) {
    console.warn('[imageBlobStore] IDB delete failed', err);
  }
}

/**
 * [PURPOSE]: One-time hydration. Loads every record from IDB into the in-memory
 *   cache so subsequent getCachedImage(id) calls are synchronous. Also folds
 *   in any localStorage fallback entries.
 */
export async function hydrateAll() {
  if (hydrated) return memCache;
  if (hasIDB()) {
    try {
      const all = await tx('readonly', (s) => s.getAll());
      if (Array.isArray(all)) {
        for (const rec of all) {
          if (rec && rec.id && rec.dataUrl) {
            memCache.set(rec.id, rec.dataUrl);
            metaCache.set(rec.id, rec);
          }
        }
      }
    } catch (err) {
      console.warn('[imageBlobStore] hydrateAll IDB read failed; using fallback only', err);
    }
  }
  // Pull localStorage fallbacks even if IDB succeeded — they may be from a
  //   previous session where IDB wasn't available.
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(FALLBACK_PREFIX)) continue;
      const id = key.slice(FALLBACK_PREFIX.length);
      if (memCache.has(id)) continue;
      const dataUrl = localStorage.getItem(key);
      if (dataUrl) memCache.set(id, dataUrl);
    }
  } catch (_) { /* ignore */ }
  hydrated = true;
  return memCache;
}

/**
 * [PURPOSE]: Return all image metadata records (for gallery list views and
 *   account-wide gallery aggregation).
 */
export async function listImages() {
  if (!hydrated) await hydrateAll();
  if (!hasIDB()) return Array.from(metaCache.values());
  try {
    const all = await tx('readonly', (s) => s.getAll());
    return Array.isArray(all) ? all : [];
  } catch (_) {
    return Array.from(metaCache.values());
  }
}

export function isHydrated() {
  return hydrated;
}

/**
 * [PURPOSE]: Return a health snapshot for the debug-bundle export (Sub-plan 4A).
 *   Reports whether IDB is available in this browser context, plus the number
 *   of image records in the store (IDB) or fallback map (localStorage).
 *   Async because IDB count requires a transaction.
 *
 * @returns {Promise<{ available: boolean, keyCount: number, error?: string }>}
 */
export async function getIdbHealth() {
  if (!hasIDB()) {
    // Count fallback entries in localStorage as the proxy metric.
    let fallbackCount = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(FALLBACK_PREFIX)) fallbackCount++;
      }
    } catch (_) { /* ignore */ }
    return { available: false, keyCount: fallbackCount };
  }
  try {
    const db = await openDB();
    const count = await new Promise((resolve, reject) => {
      let result;
      const t = db.transaction(STORE_NAME, 'readonly');
      const store = t.objectStore(STORE_NAME);
      const req = store.count();
      req.onsuccess = () => { result = req.result; };
      req.onerror = () => reject(req.error);
      t.oncomplete = () => resolve(result);
      t.onerror = () => reject(t.error);
    });
    return { available: true, keyCount: count };
  } catch (err) {
    return { available: false, keyCount: memCache.size, error: String(err) };
  }
}

/**
 * [TEST HOOK]: Reset module state. Used by jest tests that re-instantiate the
 *   store between cases. NOT for production use.
 */
export function _resetForTests() {
  memCache.clear();
  metaCache.clear();
  hydrated = false;
  dbPromise = null;
}
